import Layout from "@/containers/Layout";
import Header from "./partials/header";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getTagMentionsV4 } from "@/api/endpoints/searches";
import MentionCardNew from "@components/MentionCardNew";
import { MentionCardsProvider } from "@/containers/MentionCardsContext";
import { getTagFilterOptions, getTags } from "@/api/endpoints/tags";
import PaginationNew from "@components/PaginationNew";
import { useState } from "react";
import { Skeleton } from "@components/ui/skeleton";
import ErrorComponent from "@components/ui/error";
import { Trans, useLingui } from "@lingui/react/macro";
import { AnimatePresence, motion } from "motion/react";
import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@components/ui/empty";
import { ListFilterIcon, MessagesSquareIcon } from "lucide-react";
import { useFilterParamsV4 } from "@/hooks/useFilterParams";
import TagShareDialog from "./partials/TagShareDialog";
import { postTrackingData } from "@/api/endpoints/track";
import { EVENT } from "@/tracking_events";
import TagExportDialog from "./partials/TagExportDialog";
import DialogReach from "@components/SearchFilter/partials/DialogReach";
import { Button } from "@components/ui/button";
import SearchFilter from "@components/SearchFilter";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@components/ui/tooltip";
import SearchFilterBar from "@components/SearchFilterBar";
import { useFilterOrder } from "@components/SearchFilterBar/filterOrder";
import DialogFreeText from "@components/SearchFilter/partials/DialogFreeText";
import SearchDates from "@components/SearchDates";
import { plural } from "@lingui/core/macro";
import { useI18n } from "@/contexts/i18nContext";

export default function TagView() {
  const { t } = useLingui();
  const { formatNumber } = useI18n();

  // URL state
  const { slug = "favorited", orgUuid } = useParams<{
    orgUuid: Uuid;
    slug: string;
  }>();

  const isFavoritePage = slug === "favorited";

  const [filters, setFilters] = useFilterParamsV4();

  // Local state
  const [filterOrder, handleFilterChange] = useFilterOrder(filters, setFilters);
  const [isFreeTextOpen, setIsFreeTextOpen] = useState<boolean>(false);
  const [isReachOpen, setIsReachOpen] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [isSharing, setIsSharing] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);

  // Server state
  const { data: tagsData } = useQuery({
    queryKey: [orgUuid, "tags"],
    queryFn: () => getTags(),
    refetchOnWindowFocus: false,
  });

  const tags = tagsData?.ok
    ? tagsData.data.filter((tag) => tag.slug !== "favorited")
    : [];

  const tag = tagsData?.ok
    ? tagsData.data.find((tag) => tag.slug === slug)
    : undefined;

  const {
    data: mentionCardsData,
    isLoading: isMentionCardsLoading,
    error: mentionCardsError,
  } = useQuery({
    queryKey: [orgUuid, "tags", slug, "mentions", filters, page],
    queryFn: async () => {
      const res = await getTagMentionsV4(slug, { ...filters, page }, orgUuid);

      if (!res.ok) {
        throw new Error();
      }

      return res;
    },
  });

  const count = mentionCardsData?.ok ? mentionCardsData.data.count : undefined;

  const pageCount = mentionCardsData?.ok ? mentionCardsData.data.page_count : 0;

  const {
    data: tagFilterOptionsData,
    error: tagFilterOptionsError,
    isLoading: isTagFilterOptionsLoading,
  } = useQuery({
    queryKey: [orgUuid, "tags", slug, "filter-options"],
    queryFn: () => getTagFilterOptions(slug),
  });

  // Side-effects
  const [prevFilters, setPrevFilters] = useState(filters);
  const [prevSlug, setPrevSlug] = useState(slug);
  if (filters !== prevFilters) {
    setPrevFilters(filters);
    setPage(1);
  } else if (slug !== prevSlug) {
    setPrevSlug(slug);
    setPage(1);
  }

  // Helpers
  const heading = isFavoritePage ? t`Sparade omnämnanden` : tag?.name;

  return (
    <>
      <Layout>
        <Header
          heading={heading}
          onShare={() => {
            postTrackingData(EVENT.SHARE_TAG);
            setIsSharing(true);
          }}
          onExport={() => {
            postTrackingData(EVENT.EXPORT_TAG);
            setIsExporting(true);
          }}
        />

        <div className="border-b">
          <div className="w-full p-4 lg:p-6 max-w-7xl flex items-end justify-between">
            <div className="flex flex-col gap-1">
              <h1 className="text-2xl font-bold">{heading}</h1>
              {typeof count === "number" ? (
                <p className="text-sm text-muted-foreground">
                  {formatNumber(count)}{" "}
                  {plural(count, {
                    one: "taggat inlägg",
                    other: "taggade inlägg",
                  })}
                </p>
              ) : (
                <Skeleton className="w-36 h-5" />
              )}
            </div>

            <div className="flex items-center gap-3">
              <SearchDates
                onChange={handleFilterChange}
                selectedEndDate={filters.end_date}
                selectedStartDate={filters.start_date}
                minStartDate={
                  tagFilterOptionsData?.ok
                    ? tagFilterOptionsData.data.min_start_date
                    : undefined
                }
                maxEndDate={
                  tagFilterOptionsData?.ok
                    ? tagFilterOptionsData.data.max_end_date
                    : undefined
                }
              />

              {tagFilterOptionsData ? (
                tagFilterOptionsData.ok ? (
                  <SearchFilter
                    filterOptions={tagFilterOptionsData.data}
                    setIsFreeTextOpen={setIsFreeTextOpen}
                    setIsReachOpen={setIsReachOpen}
                    onChange={handleFilterChange}
                    value={filters}
                  >
                    <Button data-testid="filter-button">
                      <ListFilterIcon />
                      <Trans>Filter</Trans>
                    </Button>
                  </SearchFilter>
                ) : isFavoritePage ? null : (
                  <p className="text-destructive text-sm">
                    <Trans>Vi kunde inte ladda filtrena just nu.</Trans>
                  </p>
                )
              ) : isTagFilterOptionsLoading ? (
                <Tooltip>
                  <TooltipTrigger
                    render={<Button className="cursor-progress" />}
                  >
                    <ListFilterIcon />
                    <Trans>Filter</Trans>
                  </TooltipTrigger>
                  <TooltipContent>
                    <Trans>Laddar in filter</Trans>
                  </TooltipContent>
                </Tooltip>
              ) : tagFilterOptionsError ? (
                isFavoritePage ? null : (
                  <p className="text-destructive text-sm">
                    <Trans>Vi kunde inte ladda filtrena just nu.</Trans>
                  </p>
                )
              ) : null}
            </div>
          </div>

          {tagFilterOptionsData ? (
            tagFilterOptionsData.ok ? (
              <SearchFilterBar
                filterOptions={tagFilterOptionsData.data}
                setIsFreeTextOpen={setIsFreeTextOpen}
                setIsReachOpen={setIsReachOpen}
                onChange={handleFilterChange}
                innerClassName="max-w-7xl"
                filterOrder={filterOrder}
                className="border-t"
                value={filters}
              />
            ) : isFavoritePage ? null : (
              <p className="text-destructive text-sm">
                <Trans>Vi kunde inte ladda filtrena just nu.</Trans>
              </p>
            )
          ) : isTagFilterOptionsLoading && filterOrder.length > 0 ? (
            <Skeleton className="h-11 w-full rounded-none" />
          ) : tagFilterOptionsError ? (
            isFavoritePage ? null : (
              <p className="text-destructive text-sm">
                <Trans>Vi kunde inte ladda filtrena just nu.</Trans>
              </p>
            )
          ) : null}
        </div>

        <div className="max-w-7xl p-6 flex flex-col gap-8">
          <AnimatePresence mode="popLayout">
            {mentionCardsData ? (
              mentionCardsData.ok ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col gap-5"
                  key="mentions"
                >
                  {mentionCardsData.data.results.length ? (
                    <MentionCardsProvider>
                      {mentionCardsData.data.results.map((mentionCard) => {
                        return (
                          <MentionCardNew
                            mentionCard={mentionCard}
                            key={mentionCard.episode.id}
                            orgUuid={orgUuid}
                            tags={tags}
                          />
                        );
                      })}

                      <PaginationNew
                        page={page}
                        max={pageCount}
                        onChangePage={setPage}
                      />
                    </MentionCardsProvider>
                  ) : (
                    <Empty>
                      <EmptyHeader>
                        <EmptyMedia variant="icon">
                          <MessagesSquareIcon />
                        </EmptyMedia>
                        <EmptyTitle>
                          <Trans>Inga omnämnanden</Trans>
                        </EmptyTitle>
                      </EmptyHeader>
                    </Empty>
                  )}
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  key="error"
                >
                  <ErrorComponent
                    heading={t`Åh nej!`}
                    description={t`Något gick snett med att ladda omnämnanden. Vänligen försök igen senare.`}
                  />
                </motion.div>
              )
            ) : isMentionCardsLoading ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col gap-5"
                key="skeletons"
              >
                <Skeleton className="w-full h-64" />
                <Skeleton className="w-full h-64" />
                <Skeleton className="w-full h-64" />
              </motion.div>
            ) : mentionCardsError ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                key="error"
              >
                <ErrorComponent
                  heading={t`Åh nej!`}
                  description={t`Något gick snett med att ladda omnämnanden. Vänligen försök igen senare.`}
                />
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>

        <TagShareDialog
          isFavoritePage={isFavoritePage}
          onOpenChange={setIsSharing}
          orgUuid={orgUuid}
          open={isSharing}
          tag={tag}
        />

        <TagExportDialog
          isFavoritePage={isFavoritePage}
          onOpenChange={setIsExporting}
          open={isExporting}
          orgUuid={orgUuid}
          filters={filters}
          tag={tag}
        />
      </Layout>

      {tagFilterOptionsData?.ok && (
        <>
          <DialogReach
            maxReach={tagFilterOptionsData.data.filter_max_reach_value}
            value={filters.min_reach_threshold}
            onOpenChange={setIsReachOpen}
            onChange={handleFilterChange}
            open={isReachOpen}
          />

          <DialogFreeText
            onOpenChange={setIsFreeTextOpen}
            onChange={handleFilterChange}
            value={filters.freetext}
            open={isFreeTextOpen}
          />
        </>
      )}
    </>
  );
}
